import { Button, Switch, TableList } from '@root/components'
import {
  IAccountingRule,
  swapAccountingRulePriority,
  SwapAccountingRulePriorityProps,
  updateAccountingRule
} from '@root/query'
import React, { useEffect, useRef } from 'react'
import { FaCaretDown, FaCaretUp, FaEye, FaPen, FaTrash } from 'react-icons/fa'
import styled from 'styled-components'
import cn from 'classnames'
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { queryKeyForAccountingRules, useInfiniteAccountingRules } from '@root/query/hooks'
import { useBusinessContext } from '@root/context'
import { useTranslation } from 'react-i18next'
import { isWorkflowOwnedRule } from './Form/isWorkflowOwnedRule'

const ITEM_HEIGHT = 64

interface Props {
  accountingRules: IAccountingRule[]
  onScrollBottom: () => void
  onEditRule?: (rule: IAccountingRule) => void
  onDeleteRule?: (rule: IAccountingRule) => void
  /** Lay rows out in the page instead of an inner scroll area. */
  grow?: boolean
}

const AccountingRulesList: React.FC<Props> = ({
  accountingRules,
  onScrollBottom,
  onEditRule,
  onDeleteRule,
  grow = false
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const swapPriorityMutation = useMutation({
    mutationFn: (data: SwapAccountingRulePriorityProps) => swapAccountingRulePriority({ ...data }),
    ...{
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: queryKeyForAccountingRules(slug) })
        toast.success(t('settings.automation.accountingRules.priorityUpdate.success'))
      },
      onError: () => {
        toast.error(t('settings.automation.accountingRules.priorityUpdate.error'))
      }
    }
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: number; enabled: boolean }) =>
      updateAccountingRule({ slug, id: ruleId }, { enabled } as Partial<IAccountingRule>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyForAccountingRules(slug) })
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  const renderRule = (accountingRule: IAccountingRule, index: number) => (
    <RuleItem
      rule={accountingRule}
      onEdit={onEditRule}
      onDelete={onDeleteRule}
      index={index}
      accountingRules={accountingRules}
      swapPriorityMutation={swapPriorityMutation}
      toggleEnabledMutation={toggleEnabledMutation}
    />
  )

  if (grow) {
    return (
      <FlowList>
        {accountingRules.map((accountingRule, index) => (
          <FlowRow key={accountingRule.id}>{renderRule(accountingRule, index)}</FlowRow>
        ))}
        <LoadMoreSentinel itemCount={accountingRules.length} onVisible={onScrollBottom} />
      </FlowList>
    )
  }

  return (
    <TableList<IAccountingRule>
      items={accountingRules}
      itemHeight={ITEM_HEIGHT}
      onScrollBottom={onScrollBottom}
      renderItem={(accountingRule, index) => renderRule(accountingRule, index ?? 0)}
    />
  )
}

const LoadMoreSentinel: React.FC<{ itemCount: number; onVisible: () => void }> = ({
  itemCount,
  onVisible
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const onVisibleRef = useRef(onVisible)
  onVisibleRef.current = onVisible

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          onVisibleRef.current()
        }
      },
      { rootMargin: '150px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [itemCount])

  return <Sentinel ref={ref} />
}

export default AccountingRulesList

const enum Direction {
  UP = 'up',
  DOWN = 'down'
}

const RuleItem: React.FC<{
  rule: IAccountingRule
  onEdit: (rule: IAccountingRule) => void
  onDelete: (rule: IAccountingRule) => void
  index: number
  accountingRules: IAccountingRule[]
  swapPriorityMutation: UseMutationResult
  toggleEnabledMutation: UseMutationResult
}> = ({
  rule,
  onEdit,
  onDelete,
  index,
  accountingRules,
  swapPriorityMutation,
  toggleEnabledMutation
}) => {
  const rulesLength = accountingRules?.length
  const isFirst = index === 0
  const isLast = index === rulesLength - 1
  const isDisabled = !rule.enabled
  const isWorkflowRule = isWorkflowOwnedRule(rule)

  const { slug } = useBusinessContext()
  const { isRefetching: isRefetchingAllRules } = useInfiniteAccountingRules()

  const movePriority = (direction: Direction, index: number) => {
    if (direction === Direction.UP && !isFirst) {
      const targetRuleId = accountingRules[index - 1].id
      swapPriorityMutation.mutate({ slug, ruleId: rule.id, targetRuleId })
    }

    if (direction === Direction.DOWN && !isLast) {
      const targetRuleId = accountingRules[index + 1].id
      swapPriorityMutation.mutate({ slug, ruleId: rule.id, targetRuleId })
    }
  }

  return (
    <StyledItemContainer>
      <StyledPriorityContainer>
        <ButtonPlaceholder>
          {!isFirst && (
            <PriorityChangeButton
              onClick={() => movePriority(Direction.UP, index)}
              icon={<FaCaretUp />}
              disabled={swapPriorityMutation.isPending || isRefetchingAllRules}
            />
          )}
        </ButtonPlaceholder>
        <ButtonPlaceholder>
          {!isLast && (
            <PriorityChangeButton
              onClick={() => movePriority(Direction.DOWN, index)}
              icon={<FaCaretDown />}
              disabled={swapPriorityMutation.isPending || isRefetchingAllRules}
            />
          )}
        </ButtonPlaceholder>
      </StyledPriorityContainer>
      <StyledToggleContainer>
        <Switch
          id={`rule-toggle-${rule.id}`}
          checked={rule.enabled}
          onChange={() => toggleEnabledMutation.mutate({ ruleId: rule.id, enabled: !rule.enabled })}
          height={20}
          disabled={isWorkflowRule}
        />
      </StyledToggleContainer>
      <StyledName className={cn({ isDisabled })}>{rule.name}</StyledName>
      <StyledActionContainer>
        <Button
          onClick={() => onEdit(rule)}
          icon={isWorkflowRule ? <FaEye /> : <FaPen />}
          type="button"
          size="sm"
        />
        {!isWorkflowRule && (
          <Button
            onClick={() => onDelete(rule)}
            icon={<FaTrash />}
            type="button"
            intent="danger"
            size="sm"
          />
        )}
      </StyledActionContainer>
    </StyledItemContainer>
  )
}

interface PriorityChangeButtonProps {
  onClick: () => void
  icon: React.ReactElement
  disabled: boolean
}

const PriorityChangeButton: React.FC<PriorityChangeButtonProps> = ({ onClick, icon, disabled }) => {
  return (
    <Button onClick={onClick} icon={icon} type="button" isSecondary size="sm" disabled={disabled} />
  )
}

const StyledItemContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const StyledName = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: ${({ theme }) => theme.spacing.md}rem;
  padding-right: ${({ theme }) => theme.spacing.md}rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: 400;

  &.isDisabled {
    color: ${({ theme }) => theme.colors.metalGray};
    text-decoration: line-through;
  }
`

const StyledPriorityContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 75px;
  justify-content: space-between;
`

const StyledToggleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-left: auto;
  padding-left: ${({ theme }) => theme.spacing.md}rem;
`

const ButtonPlaceholder = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const FlowList = styled.div`
  display: flex;
  flex-direction: column;
`

const FlowRow = styled.div`
  display: flex;
  align-items: center;
  height: ${ITEM_HEIGHT}px;
  padding: 0 ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.neutralGray}80;
  }
`

const Sentinel = styled.div`
  height: 1px;
`
